import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Search, Calendar, Loader2, PackageOpen, Eye, ChevronDown, ShoppingBag } from 'lucide-react';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useProducerById } from '@/hooks/useMyProducers';
import { 
  useProducerOrders, 
  useNewOrderNotifier,
  useNewOrderCount,
  useOrderItems,
  OrderFilters, 
  ProducerOrder,
  formatItemCountLabel,
  formatOrderPriceWhole,
} from '@/hooks/useProducerOrders';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { ProductThumb } from '@/components/shop/ProductThumb';
import { OrderStatusBadge, OrderDetailSheet, PaymentStatusBadge } from '@/components/orders';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const BASE_TITLE = 'Farmly';

// Component to display item summary + thumbnail for an order row in the table
function OrderItemSummary({ orderId }: { orderId: string }) {
  const { data: items = [] } = useOrderItems(orderId);

  const firstProductId = items.find(i => i.product_id)?.product_id ?? null;
  const productIds = useMemo(
    () => (firstProductId ? [firstProductId] : []),
    [firstProductId],
  );
  const { data: coverMap = {} } = useProductCoverImages(productIds, 320);

  const label = formatItemCountLabel(items);
  const firstItem = items[0];
  const thumbUrl = firstProductId ? coverMap[firstProductId] : undefined;

  return (
    <div className="flex items-center gap-2">
      {firstItem && (
        <ProductThumb
          src={thumbUrl}
          title={firstItem.title}
          size="sm"
        />
      )}
      <span className="text-sm text-muted-foreground truncate max-w-[160px]">
        {label}
      </span>
    </div>
  );
}

export default function ProducerOrdersPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading: producerLoading } = useProducerById(farmId);
  
  const [activeTab, setActiveTab] = useState('new');
  const [filters, setFilters] = useState<OrderFilters>({
    status: 'new',
    dateRange: 'all',
    search: '',
  });
  
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  const { data: orders = [], isLoading, error } = useProducerOrders(farmId, filters);
  const { data: newOrderCount = 0 } = useNewOrderCount(farmId);

  const selectedOrder = useMemo(() => {
    if (!selectedOrderId) return null;
    return orders.find(o => o.id === selectedOrderId) || null;
  }, [orders, selectedOrderId]);

  const handleNewOrder = useCallback((order: ProducerOrder) => {
    setSelectedOrderId(order.id);
    setSheetOpen(true);
  }, []);

  useNewOrderNotifier(farmId, handleNewOrder);

  useEffect(() => {
    if (newOrderCount > 0) {
      document.title = `(${newOrderCount}) ${BASE_TITLE}`;
    } else {
      document.title = BASE_TITLE;
    }
    return () => { document.title = BASE_TITLE; };
  }, [newOrderCount]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setFilters(prev => ({ ...prev, status: tab }));
  };

  const handleOpenOrder = (order: ProducerOrder) => {
    setSelectedOrderId(order.id);
    setSheetOpen(true);
  };

  if (producerLoading) {
    return (
      <ProducerLayout farmId={farmId || ''} producerName="Laddar..." title="Ordrar">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId || ''} producerName={producer?.name} title="Ordrar">
      <div className="space-y-6" data-testid="producer-orders-page">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Ordrar</h1>
          <p className="text-muted-foreground mt-1">
            Hantera inkomna ordrar från din gårdsbutik
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="new" className="relative">
                  Nya
                  {newOrderCount > 0 && (
                    <Badge variant="destructive" className="ml-1.5 h-5 min-w-5 px-1.5 text-xs">
                      {newOrderCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="in_progress">Pågår</TabsTrigger>
                <TabsTrigger value="ready">Redo</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={activeTab === 'cancelled' || activeTab === 'completed' || activeTab === 'all' ? 'secondary' : 'ghost'} 
                  size="sm"
                  className="gap-1"
                >
                  Mer
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleTabChange('completed')}>Visa utlämnade ordrar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('cancelled')}>Visa avbrutna ordrar</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleTabChange('all')}>Visa alla ordrar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Sök ordernummer, kund..."
                  value={filters.search || ''}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Select
                  value={filters.dateRange || 'all'}
                  onValueChange={(value) => setFilters({ ...filters, dateRange: value as 'all' | '7d' | '30d' })}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tid</SelectItem>
                    <SelectItem value="7d">Senaste 7 dagar</SelectItem>
                    <SelectItem value="30d">Senaste 30 dagar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>Kunde inte ladda ordrar. Försök igen senare.</AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (<Skeleton key={i} className="h-16 w-full" />))}
          </div>
        )}

        {!isLoading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <PackageOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground">Inga ordrar än</h3>
            <p className="text-muted-foreground mt-1 max-w-sm">
              {activeTab === 'new' 
                ? 'Inga nya ordrar just nu. Nya ordrar visas här automatiskt när kunder handlar.'
                : activeTab === 'in_progress'
                ? 'Inga pågående ordrar.'
                : activeTab === 'ready'
                ? 'Inga ordrar redo för upphämtning.'
                : 'Inga ordrar matchar dina filter.'}
            </p>
            {activeTab === 'new' && producer?.slug && (
              <Button asChild variant="outline" className="mt-4">
                <Link to={`/producer/${producer.slug}/shop`}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Besök din butik
                </Link>
              </Button>
            )}
          </div>
        )}

        {!isLoading && !error && orders.length > 0 && (
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Kund</TableHead>
                  <TableHead>Tid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Betalning</TableHead>
                  <TableHead>Artiklar</TableHead>
                  <TableHead className="text-right">Belopp</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow 
                    key={order.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleOpenOrder(order)}
                    data-testid={`producer-order-row-${order.id}`}
                  >
                    <TableCell className="font-medium">
                      #{order.order_number || order.id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {order.customer_name || order.customer_email || 'Gäst'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(order.created_at), 'dd MMM HH:mm', { locale: sv })}
                    </TableCell>
                    <TableCell>
                      <OrderStatusBadge status={order.status} />
                    </TableCell>
                    <TableCell>
                      <PaymentStatusBadge
                        paymentStatus={order.payment_status}
                        deliveryType={order.delivery_type}
                        compact
                      />
                    </TableCell>
                    <TableCell>
                      <OrderItemSummary orderId={order.id} />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatOrderPriceWhole(order.total_amount, order.currency)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenOrder(order);
                        }}
                        data-testid={`producer-order-open-${order.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <OrderDetailSheet
        order={selectedOrder}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </ProducerLayout>
  );
}
